import React from 'react'
import { Modal, Button } from '@/shared/ui'
import { useOportunidadeModal } from '@/modules/oportunidades/context/OportunidadeModalContext'

type Mode = 'in' | 'out'

type State = {
  open: boolean
  mode: Mode | null
  symbol: string
  name: string | null
  opportunityId: string | null
  tfMandante: string | null
}

type ShowInOutOptions = {
  opportunityId?: string | number | null
  tfMandante?: string | null
}

type InOutFeedbackContextValue = {
  showIn: (symbol: string, name?: string | null, options?: ShowInOutOptions) => void
  showOut: (symbol: string, name?: string | null, options?: ShowInOutOptions) => void
}

const InOutFeedbackContext = React.createContext<InOutFeedbackContextValue | null>(null)

export function InOutFeedbackProvider({ children }: { children: React.ReactNode }) {
  const { openModal } = useOportunidadeModal()

  const [state, setState] = React.useState<State>({
    open: false,
    mode: null,
    symbol: '',
    name: null,
    opportunityId: null,
    tfMandante: null,
  })

  const close = React.useCallback(() => {
    setState((prev) => ({
      open: false,
      mode: null,
      symbol: prev.symbol,
      name: prev.name,
      opportunityId: null,
      tfMandante: null,
    }))
  }, [])

  const showIn = React.useCallback(
    (symbol: string, name?: string | null, options?: ShowInOutOptions) => {
      const opportunityId =
        options && options.opportunityId != null ? String(options.opportunityId) : null
      const tfMandante = options?.tfMandante ?? null

      setState({
        open: true,
        mode: 'in',
        symbol,
        name: name ?? null,
        opportunityId,
        tfMandante,
      })
    },
    [],
  )

  const showOut = React.useCallback(
    (symbol: string, name?: string | null, options?: ShowInOutOptions) => {
      const opportunityId =
        options && options.opportunityId != null ? String(options.opportunityId) : null
      const tfMandante = options?.tfMandante ?? null

      setState({
        open: true,
        mode: 'out',
        symbol,
        name: name ?? null,
        opportunityId,
        tfMandante,
      })
    },
    [],
  )

  const title =
    state.mode === 'in'
      ? 'Monitoramento ativado'
      : state.mode === 'out'
      ? 'Monitoramento desativado'
      : ''

  const description =
    state.mode === 'in'
      ? `Você está ativo para receber notificações desta oportunidade (${state.name || state.symbol}).`
      : state.mode === 'out'
      ? `Você não receberá mais notificações desta oportunidade (${state.name || state.symbol}).`
      : ''

  const canOpenChart = state.mode === 'in' && !!state.opportunityId && !!state.symbol

  const modal = (
    <Modal
      open={state.open && !!state.mode}
      onOpenChange={(open) => {
        if (!open) {
          close()
        }
      }}
      className="max-w-md"
    >
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">{title}</h2>
          <p className="mt-1 text-sm text-foreground/80">{description}</p>
        </div>
        <div className="flex justify-end gap-2">
          {canOpenChart && (
            <Button
              type="button"
              className="bg-lime-400 text-black hover:bg-lime-300"
              onClick={() => {
                if (!state.opportunityId || !state.symbol) {
                  close()
                  return
                }
                try {
                  openModal({
                    id: state.opportunityId,
                    symbol: state.symbol,
                    name: state.name ?? state.symbol,
                    kind: 'opportunity',
                    chartInitialTimeframe: state.tfMandante ?? undefined,
                    chartForceSignalsOverlay: true,
                  })
                } catch {
                  // noop
                } finally {
                  close()
                }
              }}
            >
              Abrir gráfico
            </Button>
          )}
          <Button type="button" variant="outline" onClick={close}>
            Entendi
          </Button>
        </div>
      </div>
    </Modal>
  )

  const value = React.useMemo<InOutFeedbackContextValue>(
    () => ({
      showIn,
      showOut,
    }),
    [showIn, showOut],
  )

  return (
    <InOutFeedbackContext.Provider value={value}>
      {children}
      {modal}
    </InOutFeedbackContext.Provider>
  )
}

export function useInOutFeedback() {
  const ctx = React.useContext(InOutFeedbackContext)
  if (!ctx) {
    throw new Error('useInOutFeedback deve ser usado dentro de InOutFeedbackProvider')
  }
  return ctx
}
